import React, { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Grid, Typography, TextField } from '@the-platform-group/component-library'
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalActions,
} from '@the-platform-group/component-library/Modal'
import { Button } from '@the-platform-group/component-library/Button'

const NODE_ID_FIELDS = [
  { name: 'pages.reportingHasParent', label: 'Reporting organization with a group' },
  {
    name: 'pages.reportingNoParent',
    label: 'Reporting organization without a group',
  },
  {
    name: 'pages.nonReportingHasParent',
    label: 'Non-reporting organization with a group',
  },
  {
    name: 'pages.nonReportingNoParent',
    label: 'Non-reporting organization without a group',
  },
]

const DEFAULT_VALUES = {
  name: '',
  description: '',
  externalId: '',
  pages: {
    reportingHasParent: '',
    reportingNoParent: '',
    nonReportingHasParent: '',
    nonReportingNoParent: '',
  },
}

const CreateTearsheetTemplateModal = ({
  open,
  templateType,
  templateToEdit,
  onClose,
  onSave,
  showNodeIds,
  isLoading,
}) => {
  if (!templateType) return null

  const { control, handleSubmit, reset } = useForm({
    mode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
  })

  const isEditing = Boolean(templateToEdit)

  const title = isEditing
    ? `Edit ${templateType.label} Template`
    : `Create ${templateType.label} Template`

  useEffect(() => {
    if (templateToEdit) {
      reset({
        name: templateToEdit.name || '',
        description: templateToEdit.description || '',
        externalId: templateToEdit.externalId || '',
        pages: {
          reportingHasParent: templateToEdit.pages?.reportingHasParent || '',
          reportingNoParent: templateToEdit.pages?.reportingNoParent || '',
          nonReportingHasParent: templateToEdit.pages?.nonReportingHasParent || '',
          nonReportingNoParent: templateToEdit.pages?.nonReportingNoParent || '',
        },
      })
    }
  }, [templateToEdit, reset])

  const handleClose = () => {
    reset(DEFAULT_VALUES)
    onClose()
  }

  const onSubmit = data => {
    const { pages, ...rest } = data
    const payload = { ...rest, templateType: templateType.key }
    if (showNodeIds) {
      payload.pages = pages
    }
    if (isEditing) {
      payload.id = templateToEdit.id
    }
    onSave(payload)
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalTitle title={title} />
      <ModalContent>
        <Grid container flexDirection="column" rowSpacing={2}>
          <Grid item>
            <Controller
              control={control}
              name="name"
              rules={{ required: 'Name is required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label="Name"
                  required
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <TextField label="Description" fullWidth multiline rows={3} {...field} />
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              control={control}
              name="externalId"
              rules={{ required: 'Workbook ID is required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label="Workbook ID"
                  required
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  {...field}
                />
              )}
            />
          </Grid>
          {showNodeIds && (
            <>
              <Grid item>
                <Typography variant="subtitle1" fontWeight="fontWeightBold" sx={{ mt: 1 }}>
                  Node IDs
                </Typography>
              </Grid>
              {NODE_ID_FIELDS.map(({ name, label }) => (
                <Grid item key={name}>
                  <Controller
                    control={control}
                    name={name}
                    rules={{ required: `${label} is required` }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label={label}
                        required
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                        {...field}
                      />
                    )}
                  />
                </Grid>
              ))}
            </>
          )}
        </Grid>
      </ModalContent>
      <ModalActions>
        <Button variant="text" onClick={handleClose}>
          Close
        </Button>
        <Button isLoading={isLoading} onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </ModalActions>
    </Modal>
  )
}

export default CreateTearsheetTemplateModal
